// i18n for S3 (step 2 · catalogue) and S4 (import detail) — Module 02 §4.
//
// Kept beside the two screens that own it and merged into `onboardingEn`
// (onboarding.i18n.ts) so the CI key-parity gate covers all four locales. ru is
// TRANSLATED (catalogue-import.ru.ts); zh/fr still render English. A key added
// here needs a Russian value in the sibling file or the gate fails the build.
//
// Three rules this file follows:
//  1. No user-facing string lives in a .tsx file — every visible word is here.
//  2. Entity NOUNS are interpolated at render time from `useTerm()` (§2.5), so a
//     KazKabel operator reads "bobbin"/"Погрешность счётчика" where a generic
//     tenant reads the base label. Copy therefore says `{{product}}`, never
//     "product".
//  3. Server codes are never rendered raw. `importShared.messages.*` mirrors the
//     closed row-message catalog (§2.7 + the catalogue validators) and
//     `importShared.problems.*` the RFC 9457 problem codes the import endpoints
//     raise (§3.3, §9.3, §9.15); an unmapped code degrades to the code itself
//     through i18next `defaultValue`, never to a blank line.

export const catalogueImportEn = {
  // ===========================================================================
  // Shared import vocabulary (used by BOTH S3 and S4)
  // ===========================================================================
  importShared: {
    // The eleven import kinds (§2.6). These name an import LANE, not an entity,
    // so they are dictionary copy; the entity nouns inside prose interpolate.
    kinds: {
      products: "Products",
      materials: "Materials",
      boms: "Bills of materials",
      routings: "Routings",
      customers: "Customers",
      suppliers: "Suppliers",
      opening_stock: "Opening stock",
      open_sales_orders: "Open sales orders",
      open_purchase_orders: "Open purchase orders",
      machines: "Machines",
      people: "People",
    },
    // import_jobs.status (§2.6 / §3.3 state machine).
    jobStatus: {
      uploaded: "Uploaded",
      parsing: "Reading the file",
      mapping: "Needs column mapping",
      validated: "Checked",
      committing: "Loading",
      committed: "Loaded",
      failed: "Failed",
      discarded: "Discarded",
    },
    // import_job_rows.status (§2.7).
    rowStatus: {
      pending: "Not checked",
      valid: "OK",
      warning: "Warning",
      error: "Error",
      committed: "Loaded",
      skipped: "Skipped",
    },
    severity: {
      error: "Error",
      warning: "Warning",
    },
    counters: {
      total: "Rows",
      valid: "OK",
      warning: "Warnings",
      error: "Errors",
      committed: "Loaded",
      skipped: "Skipped",
      // Shown instead of a number before the commit worker has run: a derived
      // "skipped" count on an uncommitted job is arithmetic, not a fact. (It
      // lived under `rowStatus` — which has no such row status — so the cell
      // rendered the raw key; the referenced-key gate found it.)
      notYet: "Not known until the file is loaded",
    },
    duplicatePolicy: {
      label: "If a row already exists",
      help: "Set at upload and fixed for this import. Repeats inside one file are always an error.",
      fixed: "Set at upload. To change it, discard this import and upload the file again.",
      reject: "Reject the row",
      rejectHelp: "The row is marked as an error and nothing is overwritten.",
      update: "Update what is there",
      updateHelp: "The existing record is replaced from the file.",
      skip: "Skip the row",
      skipHelp: "The existing record is left exactly as it is.",
    },
    // Target-field labels for the mapping editor. Only the kinds whose owning
    // module is wired (the catalogue lanes) have a field catalog; the others get
    // free-text mapping until their module ships, so no field name is invented.
    fields: {
      sku: "SKU",
      name: "Name",
      kind: "Kind",
      base_uom: "Base unit",
      attribute_schema: "Attribute set",
      tracking: "Tracking",
      make_or_buy: "Make or buy",
      price_unit_qty: "Price per quantity",
      product_sku: "SKU of the {{product}}",
      version: "Version",
      component_sku: "SKU of the component",
      qty: "Quantity",
      uom: "Unit",
      line_no: "Line number",
      scrap_pct: "Component scrap %",
      consume_at_step_no: "Consumed at step",
      is_optional: "Optional line",
      facility_id: "{{facility}}",
      output_qty: "Output quantity",
      output_uom: "Output unit",
      is_phantom: "Phantom",
      step_no: "Step number",
      work_center: "{{workCenter}}",
      run_minutes: "Run minutes",
      setup_minutes: "Setup minutes",
      run_basis: "Run basis",
      batch_qty: "Batch quantity",
      setup_key: "Setup family",
      operation_scrap_pct: "Operation scrap %",
      // The customers lane (P3-E2 PR E2-2c, spec 09 §3.15's Columns
      // paragraph). The words are the customer form's own (sales.create,
      // sales.c360), so a column maps onto the field a sales manager already
      // knows by that name.
      customer_no: "Customer number",
      legal_name: "Legal name",
      tier: "Price tier",
      display_name: "Display name",
      tax_id: "Tax ID",
      country: "Country",
      default_currency: "Currency",
      credit_limit: "Credit limit",
      credit_terms_days: "Payment terms, days",
      preferred_channel: "Preferred channel",
      preferred_locale: "Preferred language",
      owner_email: "Owner's email",
      contact_name: "Contact name",
      contact_position: "Contact position",
      phone: "Phone",
      email: "Email",
      tags: "Tags",
      notes: "Note",
      billing_line1: "Billing address, line 1",
      billing_line2: "Billing address, line 2",
      billing_city: "Billing city",
      billing_region: "Billing region",
      billing_postal: "Billing postcode",
      billing_country: "Billing country",
      // The open orders lane (spec 09 §3.15b). `customer_no`, `tax_id`,
      // `product_sku`, `qty` and `uom` above serve it as they are.
      order_ref: "Order number in your file",
      unit_price: "Unit price",
      currency: "Currency",
      promise_date: "Promised date",
      facility_code: "{{facility}} code",
      customer_po_ref: "Customer PO number",
      ordered_on: "Order date",
    },
    fieldUnmapped: "Not imported",
    fieldAttribute: "Attribute column",
    fieldCustom: "Field name",
    fieldCustomHint: "Type the field name exactly as the import expects it.",
    // Row-level message codes (§2.7 closed catalog + the catalogue validators).
    messages: {
      required: "This column is required and was empty.",
      required_missing: "This column is required and was empty.",
      bad_number: "This value is not a number.",
      unknown_unit: "This unit does not exist in your unit list.",
      unknown_uom: "This unit does not exist in your unit list.",
      uom_dimension_mismatch: "These two units measure different things and cannot be converted.",
      invalid_dimension: "This is not a unit dimension we recognise.",
      precision_out_of_range: "The decimal precision is outside the allowed range.",
      factor_not_positive: "A conversion factor must be greater than zero.",
      unknown_reference: "This refers to something that does not exist yet.",
      unknown_component_sku: "No {{product}} with this SKU exists, and no earlier row creates it.",
      missing_bom_parent_sku: "The parent SKU is missing, so this line has nothing to belong to.",
      unknown_attribute_schema: "This attribute set does not exist.",
      unknown_work_center: "No {{workCenter}} with this code exists.",
      unknown_import_kind: "This import kind is not one we recognise.",
      invalid_kind: "This is not one of the allowed kinds.",
      duplicate_in_file: "The same key appears earlier in this file.",
      duplicate_sku_in_file: "The same SKU appears earlier in this file.",
      duplicate_in_db: "A record with this key already exists.",
      group_has_errors:
        "Another row of the same group has an error, so the whole group is held back.",
      money_not_integer: "This amount has more decimal places than the currency allows.",
      currency_mismatch: "This currency is not your company's currency.",
      length_exceeds_capacity: "This length is more than the container can hold.",
      qty_not_positive: "The quantity must be greater than zero.",
      // Opening-stock lane (Module 06, spec 06 §3.10).
      location_not_receivable:
        "Stock cannot be received into this location — use a bin, a receiving area or quarantine.",
      measure_uom_invalid: "A measured container is tracked in m, kg or pcs.",
      value_not_divisible:
        "This value does not divide evenly over the quantity, so a per-unit cost cannot be stored exactly.",
      bare_container_invalid:
        "An empty container row cannot carry a quantity or a value — it is registered, not received.",
      valued_uom_not_base:
        "A row with a value must use the product's base unit — costs are stored per base unit.",
      idempotency_conflict:
        "This row's ledger slot is occupied by an unrelated record — contact support before re-loading.",
      scrap_out_of_range: "Scrap must be between 0 and 100 percent.",
      run_minutes_invalid: "Run minutes must be zero or more.",
      setup_minutes_invalid: "Setup minutes must be zero or more.",
      batch_qty_required: "A batch basis needs a batch quantity.",
      outsource_code_required: "An outsourced step needs a supplier code.",
      step_order: "Step numbers must increase down the file.",
      // The customers lane (P3-E2 PR E2-2c), `customersImportRowCodes` in
      // packages/contracts/src/onboarding/schemas.ts. `required`,
      // `duplicate_in_db` and `money_not_integer` above serve it as they are.
      // Every code is held to the contract's list by
      // imports/__tests__/import-model.test.ts.
      customer_not_found:
        "No customer you can open has this customer number. Check the number, or leave it empty to create a new customer.",
      customer_archived:
        "This customer number belongs to an archived customer, so the row cannot update it.",
      ambiguous_natural_key:
        "Several of your customers share this tax ID, so the row cannot tell which one to update. Add the customer number.",
      owner_not_member: "No active member of this company with access to customers has this email.",
      owner_not_allowed: "Only a group or facility admin can name a customer's owner.",
      // Field-neutral on purpose: the customers lane raises PERMISSION_DENIED
      // for a credit cell on a new customer, for a credit or tier change on an
      // update row, and for any 403 the commit meets (spec 09 §3.15), and the
      // grid names the column the message sits on.
      permission_denied:
        "Your role cannot set or change this value. Leave the cell empty, or as the customer already has it.",
      identity_taken: "Another contact already holds this phone or email.",
      tag_archived: "This tag was archived while the file was loading, so it was not added.",
      import_importer_not_permitted:
        "The person who uploaded this file can no longer add customers, so the row was not loaded.",
      possible_duplicate:
        "A customer with a similar name or the same tax ID already exists. The row still loads; check it is not the same company.",
      tag_unknown: "There is no active tag with this name, so it was skipped.",
      ignored_on_update:
        "This row updates a customer, so its contact and owner cells are not written.",
      note_redacted:
        "The note in this row was removed because a note with the same words was redacted.",
      // A row whose cell fails the customer form's own checks carries the
      // check's code (zod's issue codes, D-325). One sentence each, written
      // for the cell rather than for a programmer.
      invalid_type: "This value is the wrong kind for this column.",
      invalid_literal: "This value is not the one this column expects.",
      custom: "This value is not accepted here.",
      invalid_union: "This value matches none of the forms this column accepts.",
      invalid_union_discriminator: "This value matches none of the forms this column accepts.",
      invalid_enum_value: "This is not one of the values this column accepts.",
      unrecognized_keys: "This row carries a field the customer form does not have.",
      invalid_arguments: "This value is not accepted here.",
      invalid_return_type: "This value is not accepted here.",
      invalid_date: "This is not a valid date.",
      invalid_string: "This text is not in the form this column expects.",
      too_small: "This value is too short or too small.",
      too_big: "This value is too long or too large.",
      invalid_intersection_types: "This value is not accepted here.",
      not_multiple_of: "This number is not a whole step of what the column allows.",
      not_finite: "This number is not a finite number.",
    },
    // A lane's own sentence for a row code, read before `messages` above
    // (labels.ts). The open orders lane's codes are `openSalesOrdersImportRowCodes`
    // in packages/contracts/src/onboarding/schemas.ts; the sentences speak of
    // orders where the shared ones speak of customers.
    kindMessages: {
      open_sales_orders: {
        required:
          "This column is required and was empty. A row also needs a customer number or a tax ID.",
        customer_not_found:
          "No customer you can open has this customer number or tax ID. Load the customer first, or fix the number.",
        customer_archived:
          "This customer is archived. Restore them, or move the order to the customer they were merged into.",
        ambiguous_natural_key:
          "Several of your customers share this tax ID, so the row cannot tell whose order it is. Add the customer number.",
        product_not_found: "No {{product}} with this SKU exists.",
        product_archived: "This {{product}} is discontinued, so no order can be loaded for it.",
        uom_not_found: "This unit does not exist in your unit list.",
        uom_not_convertible:
          "This unit cannot be converted into the {{product}}'s base unit, so the quantity cannot be read.",
        facility_not_found: "No {{facility}} has this code.",
        facility_not_eligible:
          "This {{facility}} cannot ship this order: it is inactive, or not one you work in.",
        facility_unresolved:
          "The order names no {{facility}}, and none you work in can ship it. Add a code to the row.",
        ordered_on_in_future: "The order date is later than today.",
        promise_date_before_ordered_on: "The promised date is earlier than the order date.",
        money_not_integer: "This price has more decimal places than the currency allows.",
        ambiguous_decimal:
          "A number like 1,500 reads as one and a half in some files and fifteen hundred in others. Write it as 1500 or 1.5.",
        currency_mismatch:
          "This currency is not the customer's currency. An order is priced in the customer's currency.",
        group_header_conflict:
          "Rows of this order disagree on a value the whole order shares, such as the customer, the {{facility}}, the order date or the PO number.",
        order_ref_already_imported:
          "This customer already has an order loaded under this number, so it would be loaded twice. Cancel that order first if this one replaces it.",
        order_ref_already_imported_named:
          "This customer already has order {{order_no}} loaded under this number, so it would be loaded twice. Cancel order {{order_no}} first if this one replaces it.",
        vat_mode_mixed:
          "These lines use price lists that show VAT differently. Load them as two orders.",
        permission_denied:
          "The person who uploaded this file cannot place this order, so it was not loaded.",
        import_importer_not_permitted:
          "The person who uploaded this file can no longer place orders, so the row was not loaded.",
        resource_busy:
          "Someone was changing this customer while the order was loading, so it was not loaded. Load these rows again.",
        order_not_written:
          "This order could not be written, and nothing of it was loaded. Load these rows again, and tell support if it fails twice.",
        group_has_errors:
          "Another row of this order has an error, so the whole order is held back.",
      },
    },
    // Problem codes raised by the import endpoints (§3.3, §9.3, §9.15).
    problems: {
      hasErrorsTitle: "Some rows have errors",
      hasErrorsBody:
        "Fix the rows and check again, or turn on “load the good rows only” to skip them.",
      mappingIncompleteTitle: "Required columns are not mapped",
      mappingIncompleteBody: "Map these before checking the file:",
      kindNotAvailableTitle: "This import isn't available",
      kindNotAvailableBody: "This lane isn't available in this workspace. Nothing has been loaded.",
      kindNotAvailableFallback: "the module that owns it",
      fileTooLargeTitle: "The file is too big",
      fileTooLargeBody:
        "The limit is {{sizeMb}} MB and {{maxRows}} rows. Split the file and upload the parts one at a time.",
      badEncodingTitle: "The file could not be read",
      badEncodingBody:
        "It is neither UTF-8 nor Windows-1251. Re-save it as .xlsx or as CSV with UTF-8 encoding.",
      emptyFileTitle: "The file has no rows",
      emptyFileBody: "The first sheet needs a header row and at least one row of data.",
      mergedCellsTitle: "The sheet has merged cells",
      mergedCellsBody: "Unmerge the cells and upload the file again.",
      financeRequiredTitle: "Loading valued stock needs the finance role",
      financeRequiredBody:
        "This import posts value to the opening journal, so a group admin or someone with the finance role has to load it.",
      stateInvalidTitle: "This step does not apply right now",
      stateInvalidBody:
        "The import moved on while this page was open. Reload it to see where it is.",
      notFoundTitle: "Import not found",
      // Since E2-2c a sales importer reads only the customers jobs it created
      // (spec 02 §7, D-325 ruling 6), and another user's job answers the same
      // 404 another tenant's does, so the sentence names both causes.
      notFoundBody: "It was discarded, or it is not an import you can open.",
      facilityRequiredTitle: "Choose a {{facility}} first",
      facilityRequiredBody: "This import is per {{facility}}, so it cannot be uploaded group-wide.",
      forbiddenTitle: "You do not have access to this import",
      forbiddenBody: "Ask a group admin to run it, or to give you the matching import permission.",
      // 422 IMPORT_IMPORTER_NOT_PERMITTED (spec 09 §3.15 and §4.18, D-325
      // ruling 2): about the job's CREATOR, whoever pressed the button.
      importerNotPermittedTitle: "This file can no longer be checked or loaded",
      importerNotPermittedBody:
        "The uploader can no longer add customers, so nothing changed. Discard this and upload again from an account that can.",
      importerNotPermittedOrdersBody:
        "The uploader can no longer place orders, so nothing changed. Discard this and upload again from an account that can.",
      genericTitle: "That did not work",
      genericBody: "The request could not be completed. Try again.",
    },
    // §7 field-level redaction: money columns arrive nulled with the field named
    // in `redacted_fields[]`. Render a lock, never a blank and never a zero.
    redacted: "Hidden",
    redactedHint: "Money columns are hidden for your role.",
    redactedNotice:
      "Money columns in this file are hidden for your role. They are still imported — you just cannot see them here.",
  },

  // ===========================================================================
  // S3 · Step 2 — Catalogue (/onboarding/catalogue)
  // ===========================================================================
  catalogueStep: {
    tabs: {
      label: "How you want to load your catalogue",
      manual: "By hand",
      import: "From a spreadsheet",
    },

    manual: {
      title: "Build it here",
      blurb: "Create a {{product}} from a template, then add its {{bom}} and {{routingStep}} list.",
      note: "Opening a {{product}} takes you into the catalogue module. Use the step rail above to come back.",
      openCatalogue: "Open the full catalogue",
    },

    importTab: {
      title: "Load a spreadsheet",
      blurb: "Fill in the template, drop it here, check it, then load.",
      kindLabel: "What are you loading?",
      templateTitle: "1 · Get the template",
      templateHint:
        "A header row with exactly the columns this import understands. Products and materials also get the attribute columns your industry pack installed.",
      download: "Download template (.csv)",
      uploadTitle: "2 · Upload your file",
      notPermitted: "You cannot run this import",
      notPermittedBody:
        "Your role can't run this import. Ask a group or facility admin to run it or give you access.",
    },

    dropzone: {
      label: "Drop your file here",
      hint: "or choose one — .xlsx or .csv, up to {{sizeMb}} MB",
      browse: "Choose a file",
      chosen: "{{name}} · {{size}}",
      clear: "Remove",
      accept: ".xlsx, .xlsm or .csv",
    },

    upload: {
      start: "Upload and check",
      creating: "Preparing the upload…",
      uploading: "Uploading…",
      starting: "Reading the file…",
      failedTitle: "The upload did not finish",
      failedBody: "Nothing was loaded. Check your connection and try the upload again.",
      retry: "Try the upload again",
      retryReusesJob:
        "Trying again re-sends the same file to the same upload — it does not create a second import.",
      progress: "{{percent}}% — {{sent}} of {{total}}",
      cancel: "Cancel the upload",
      // Spec 09 §4.18 and spec 02 §4: an upload needs the network for its
      // presigned PUT, so it is disabled while the browser is offline.
      offline: "You are offline. Uploading starts again when the connection returns.",
    },

    fileProblem: {
      tooLarge:
        "This file is {{size}}. The limit is {{sizeMb}} MB — split it and upload the parts.",
      unsupported: "Only .xlsx, .xlsm and .csv files can be imported. This one is {{name}}.",
    },

    jobs: {
      title: "Your imports",
      filterKind: "Show",
      filterAny: "Everything",
      loading: "Loading your imports…",
      errorTitle: "Could not load your imports",
      errorDetail: "The import history could not be loaded. Try again.",
      emptyTitle: "No imports yet",
      emptyBody: "Download the template, fill it in, and drop it here.",
      col: {
        kind: "What",
        file: "File",
        status: "Status",
        rows: "Rows",
        counts: "OK / warnings / errors",
        created: "Uploaded",
        actions: "Actions",
      },
      open: "Open",
      discard: "Discard",
      discarding: "Discarding…",
      discardConfirm: "Discard this import? Nothing that has already been loaded is undone.",
      prev: "Previous",
      next: "Next",
      showing: "Showing {{count}}",
    },

    progress: {
      title: "Catalogue readiness",
      blurb: "The go-live checks that this step moves.",
      neverEvaluated:
        "These have not been checked yet. Run the check to see where your catalogue stands.",
      evaluate: "Run the check",
      evaluating: "Checking…",
      lastEvaluated: "Checked {{time}}",
      never: "Not checked yet",
      openChecklist: "Open the full go-live checklist",
      errorTitle: "Could not load the go-live checks",
      blocking: "Blocking",
      loading: "Loading the go-live checks…",
    },

    // Titles for the four `catalogue.*` go-live checks (§3.6). Dots in an item
    // key are i18next path separators, so keys here use underscores.
    items: {
      catalogue_products_min: "At least one {{product}} exists",
      catalogue_boms_cover_products: "Most made {{product}} items have a {{bom}}",
      catalogue_routings_cover_products: "Most made {{product}} items have a routing",
      catalogue_routings_bound: "Every routing step points at a real {{workCenter}}",
    },
  },

  // ===========================================================================
  // S4 · Import detail (/onboarding/imports/:id)
  // ===========================================================================
  importDetail: {
    back: "Back to the catalogue step",
    loading: "Loading this import…",
    errorTitle: "Could not load this import",
    errorDetail: "The import could not be loaded. Try again.",
    notPermittedTitle: "Read-only",
    notPermittedBody: "You can look at this import, but your role cannot change or load it.",

    meta: {
      kind: "What",
      file: "File",
      size: "Size",
      created: "Uploaded",
      policy: "If a row already exists",
      facility: "{{facility}}",
      none: "—",
      validated: "Checked {{time}}",
      committed: "Loaded {{time}}",
    },

    // status: uploaded
    parse: {
      title: "The file is uploaded",
      body: "Reading it now — this takes a moment for a big sheet.",
      start: "Read the file",
      starting: "Reading…",
      failedTitle: "Could not start reading the file",
      failedBody:
        "Nothing was loaded. Try again, or discard this import and upload the file again.",
    },

    // status: parsing
    parsing: {
      title: "Reading your file",
      body: "We are pulling out the header row and every data row. This page updates itself.",
    },

    // status: mapping
    mapping: {
      title: "Match your columns",
      blurb: "Recognised columns are matched. Check the rest, then run the checks.",
      colSource: "Column in your file",
      colSample: "First value",
      colTarget: "Goes into",
      colState: "",
      auto: "Matched for you",
      edited: "Changed",
      required: "Required",
      save: "Save the matching",
      saving: "Saving…",
      saved: "Matching saved",
      missingTitle: "Some required columns are not matched yet",
      missingBody: "Match these before running the checks:",
      duplicateTitle: "Two columns point at the same field",
      duplicateBody: "Each field can take only one column. Fix these:",
      validate: "Check the file",
      validating: "Checking…",
      noHeadersTitle: "No columns to match",
      noHeadersBody:
        "The file was read but produced no header row. Discard it and upload it again.",
      recallBody:
        "You matched a file with exactly these columns before. Apply that matching to save re-doing it?",
      recallCta: "Use my last matching",
    },

    // Pre-commit impact (§4 S4) + the discard confirmation.
    impact: {
      title: "What loading will do",
      willLoad: "{{count}} rows will be written to your {{kind}}.",
      willLoad_one: "{{count}} row will be written to your {{kind}}.",
      withWarnings: "{{count}} of them carry a warning — they load, but check them afterwards.",
      withWarnings_one: "{{count}} of them carries a warning — it loads, but check it afterwards.",
      willSkip: "{{count}} rows with errors will be skipped and left in this import.",
      willSkip_one: "{{count}} row with errors will be skipped and left in this import.",
      blockedByErrors:
        "{{count}} rows have errors. Fix them and check again, or tick the box above to load the rest without them.",
      blockedByErrors_one:
        "{{count}} row has errors. Fix it and check again, or tick the box above to load the rest without it.",
      duplicates: "Rows that already exist: {{policy}}.",
      irreversible:
        "Loading cannot be undone from here. To reverse it you would have to delete the records in the module that owns them.",
    },
    discard: {
      confirmTitle: "Discard this import?",
      confirmBody:
        "\u201c{{file}}\u201d and its {{rows}} checked rows are thrown away. Nothing that has already been loaded is undone.",
    },

    // status: validated
    validated: {
      title: "Check results",
      allGood: "Every row passed. Load them when you are ready.",
      someBad:
        "{{errors}} of {{total}} rows have errors. Fix them and check again, or load only the good rows.",
      revalidate: "Check again",
      editMapping: "Change the column matching",
      errorReport: "Download the error report (.xlsx)",
      commit: "Load into the catalogue",
      // The customers lane loads into the customer book, not the catalogue
      // (spec 09 §4.18), so its job page says where the rows go.
      commitCustomers: "Load into the customer book",
      commitOrders: "Load the orders into sales",
      committing: "Loading…",
      allowPartial: "Load the good rows and skip the ones with errors",
      allowPartialHelp:
        "Skipped rows stay in this import, so you can fix the file and load them later.",
      // A grouped lane commits each order whole (spec 02 §3.3 step 5).
      allowPartialHelpGrouped:
        "An order with any row in error is skipped whole, so no order loads with lines missing. Skipped orders stay in this import.",
      discard: "Discard this import",
      discarding: "Discarding…",
      discardConfirm: "Discard this import? The file and its check results are thrown away.",
      fixHint:
        "Rows can't be edited here. Fix a wrong column in the matching above, or a wrong value in your file and upload again.",
    },

    // status: committing
    committing: {
      title: "Loading your rows",
      body: "Each row is written through the module that owns it. This page updates itself.",
      progress: "{{done}} of {{total}} rows",
    },

    // status: committed
    committed: {
      title: "Loaded",
      // Kind-neutral since the customers lane (E2-2c) runs this same screen:
      // its rows land in the sales customer book, not the catalogue.
      body: "{{committed}} rows were loaded.",
      skipped: "{{count}} rows were skipped.",
      skipped_one: "{{count}} row was skipped.",
      errors: "{{count}} rows had errors and were not loaded.",
      errors_one: "{{count}} row had errors and was not loaded.",
      backToStep: "Back to the setup step",
      partialHint: "Fix the skipped rows in your spreadsheet and upload it again as a new import.",
      seeOrders: "See the loaded orders",
    },

    // status: failed
    failed: {
      title: "This import failed",
      body: "Nothing was loaded. The file could not be read end to end.",
      causesTitle: "The usual causes:",
      causeEncoding: "The CSV is not UTF-8 or Windows-1251 — re-save it as .xlsx.",
      causeTooLarge: "The file is over {{sizeMb}} MB or {{maxRows}} rows — split it.",
      causeMerged: "The sheet has merged cells — unmerge them.",
      causeEmpty: "The first sheet has a header row but no data rows.",
      whatNext: "Fix the file and upload it again from the catalogue step.",
    },

    // status: discarded
    discarded: {
      title: "This import was discarded",
      body: "Nothing was loaded. Upload the file again from the catalogue step to start over.",
    },

    rows: {
      title: "Rows",
      loading: "Loading rows…",
      errorTitle: "Could not load the rows",
      errorDetail: "The rows could not be loaded. Try again.",
      emptyTitle: "No rows",
      emptyBody: "The file was read but produced no data rows.",
      noMatchTitle: "No rows match",
      noMatchBody: "No row in this import matches that filter.",
      filterAll: "All",
      filterValid: "OK",
      filterWarning: "Warnings",
      filterError: "Errors",
      filterCommitted: "Loaded",
      filterSkipped: "Skipped",
      searchLabel: "Search the rows",
      searchPlaceholder: "Any value in the row…",
      colRow: "Row",
      colStatus: "Status",
      colMessages: "What is wrong",
      messagesNone: "—",
      prev: "Previous",
      next: "Next",
      showing: "Showing {{count}}",
      moreColumns: "+{{count}} more columns — download the error report to see them all",
      moreColumns_one: "+{{count}} more column — download the error report to see it",
      rowLabel: "Row {{number}}",
    },
  },
} as const;
